import { NgOptimizedImage } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, input, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { I18nService } from '../../../core/i18n/i18n.service';
import type { LanguageOption } from '../../../core/i18n/i18n.models';

@Component({
  selector: 'app-language-switcher',
  templateUrl: './language-switcher.html',
  styleUrl: './language-switcher.scss',
  imports: [SelectModule, FormsModule, NgOptimizedImage],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LanguageSwitcher implements OnInit {
  private readonly i18n = inject(I18nService);
  private readonly flagCache: HTMLImageElement[] = [];

  readonly appendTo = input<'self' | 'body'>('body');

  readonly languages = signal<LanguageOption[]>([
    { code: 'en', label: 'English', flagSrc: 'assets/flags/usa.PNG' },
    { code: 'ar', label: 'Arabic', flagSrc: 'assets/flags/Arabic.PNG' },
    { code: 'ku', label: 'Kurdish', flagSrc: 'assets/flags/Kurdish.png' },
    { code: 'ru', label: 'Russian', flagSrc: 'assets/flags/Russian.png' },
    { code: 'fa', label: 'Persian', flagSrc: 'assets/flags/Persion.PNG' },
    { code: 'tr', label: 'Turkish', flagSrc: 'assets/flags/Turkish.PNG' },
    { code: 'es', label: 'Spanish', flagSrc: 'assets/flags/Spanish.PNG' },
    { code: 'pt', label: 'Portuguese', flagSrc: 'assets/flags/Portuguese.PNG' },
    { code: 'fr', label: 'French', flagSrc: 'assets/flags/French.PNG' },
    { code: 'de', label: 'German', flagSrc: 'assets/flags/German.PNG' },
  ]);

  readonly selectedLanguage = signal<LanguageOption>(this.languages()[0]!);

  constructor() {
    const current = this.languages().find((l) => l.code === this.i18n.language());
    if (current) {
      this.selectedLanguage.set(current);
    }
  }

  ngOnInit(): void {
    this.preloadFlags();
  }

  onLanguageChange(lang: LanguageOption): void {
    this.selectedLanguage.set(lang);
    this.i18n.setLanguage(lang.code);
  }

  // Preload all flag images so they're browser-cached before the dropdown opens.
  // This prevents the per-open network waterfall visible in the screenshot (144 requests).
  private preloadFlags(): void {
    this.languages().forEach(({ flagSrc }) => {
      const img = new Image();
      img.src = flagSrc;
      this.flagCache.push(img); // hold reference = stays in memory cache
    });
  }
}